import {
  Column,
  Filter,
  Grid,
  Pagination,
  ValidFilterValues
} from '@120wateraudit/envirio-components'
import { DocumentNode } from 'graphql'
import { debounce, isEmpty, omit } from 'lodash'

import { ApolloClient, FetchPolicy, QueryHookOptions } from '@apollo/client'
import { Query } from '@apollo/client/react/components'
import React from 'react'
import { CheckboxProps, GridProps } from 'semantic-ui-react'
import { piBaseClient } from 'src/apollo-clients'

export interface FilterMap {
  [filterName: string]: Filter
}
export interface GQLGridProps {
  additionalMenuItems?: undefined | React.ReactNode
  availableFilters?: FilterMap
  client?: ApolloClient<any>
  columns: Column[]
  defaultFilters?: Filter[]
  defaultSorts?: { [key in string]: SortDirection }
  displaySearch?: boolean
  entityName?: string
  exportURL?: string
  fetchPolicy?: FetchPolicy
  fetchURL?: string
  gridOptions?: Partial<GridProps>
  hideShowingText?: boolean
  isCompleted?: boolean
  isOnePage?: boolean
  noResultsFound?: JSX.Element
  pageIndexBase?: number
  parentRef?: any
  pluralEntityName?: string
  query: DocumentNode
  searchRowElement?: JSX.Element
  selectable?: boolean
  selectedItems?: any[]
  selectionKey?: any
  sorts?: { [key in string]: SortDirection }
  style?: React.CSSProperties
  topRightFilter?: Filter
  variables: QueryHookOptions['variables']
  getGridFilters?: (filters: { [key in string]: Filter['value'] }) => void
  getSearchValue?: (searchValue: string) => void
  onFilterChanged?: (
    currentState: GQLGridState
  ) => undefined | Partial<GQLGridState>
  onSelectedItemsChange?: (selectedItems: any[]) => void
  renderRow: (item: any, index?: number, columns?: Column[]) => React.ReactNode
  toggleCompleted?: () => void
  totalGetter?(data: any): number
}

interface GQLGridState {
  data: any[]
  exportURL?: string
  filterOperators: { [key in string]: string }
  filterValues2: { [key in string]: any }
  filters: { [key in string]: ValidFilterValues }
  filtersArray?: Filter[]
  isFetching?: boolean
  pageIndexBase: number
  pageNumber: number
  pageSize: number
  searchValue: undefined | string
  selectedItems?: any[]
  sorts: { [key in string]: SortDirection }
  topRightFilter: undefined | { [key in string]: Filter['value'] }
  valueToBeSearched: undefined | string
}

export class GQLGrid extends React.Component<GQLGridProps, GQLGridState> {
  query: any
  variables: any
  refetch: any

  constructor(props: GQLGridProps) {
    super(props)

    if (props.parentRef) {
      props.parentRef(this)
    }

    const filters = {}

    let topRightFilter: GQLGridState['topRightFilter']

    if (props.topRightFilter) {
      filters[props.topRightFilter.key] = props.topRightFilter.value

      topRightFilter = {
        [props.topRightFilter.key]: props.topRightFilter.value
      }
    }

    this.state = {
      data: [],
      filterOperators: {},
      filterValues2: {},
      filters,
      filtersArray: this.props.defaultFilters || [],
      pageIndexBase: this.props.pageIndexBase || 0,
      pageNumber: 0,
      pageSize: 10,
      searchValue: undefined,
      selectedItems: [],
      sorts: this.props.defaultSorts || {},
      topRightFilter,
      valueToBeSearched: undefined
    }
  }

  handleColumnClicked = (refetch: () => void) => (column: Column) => {
    if (!column.sortable) {
      return
    }

    const { sorts = {} } = this.state

    if (sorts[column.key]) {
      if (sorts[column.key] === 'ASC') {
        sorts[column.key] = 'DESC'
      } else {
        delete sorts[column.key]
      }
    } else {
      sorts[column.key] = 'ASC'
    }

    this.setState({ sorts }, refetch)
  }

  handlePageChanged =
    (refetch: () => void) =>
    (_, { activePage: page }: any) => {
      this.setState({ pageNumber: page - 1 }, refetch)
    }

  handlePageSizeChanged =
    (refetch: () => void) =>
    ({ value: pageSize }: { value: any }) => {
      this.setState({ pageNumber: 0, pageSize }, refetch)
    }

  handleTopRightFilterChanged = (refetch: () => void) => (_, value) => {
    const { topRightFilter } = this.props

    if (topRightFilter) {
      this.setState(
        {
          filters: {
            ...this.state.filters,
            [topRightFilter.key]: value
          },
          pageNumber: 0,
          topRightFilter: {
            [topRightFilter.key]: value
          }
        },
        refetch
      )
    }
  }

  getExportVariables = () => {
    const { searchValue, sorts = {} } = this.state

    return {
      search: searchValue,
      sorts
    }
  }

  fetchData = async () => {
    this.forceUpdate()
  }

  public getVariables = () => {
    const { variables } = this.props
    const {
      filters,
      pageNumber,
      pageSize,
      sorts,
      topRightFilter: topRightFilterState,
      valueToBeSearched
    } = this.state

    return {
      ...variables,
      params: {
        filters: {
          ...filters,
          ...(topRightFilterState || {})
        },
        pageNumber,
        pageSize,
        search: valueToBeSearched,
        sorts
      }
    }
  }

  public getRefetch = () => {
    if (this.refetch && typeof this.refetch === 'function') {
      this.refetch()
    }
  }

  buildFetchURL = (exporting = false): string => {
    let sortStr = ''
    let outURL = exporting
      ? this.props.exportURL || ''
      : this.props.fetchURL || ''

    const { filtersArray, topRightFilter } = this.state
    const { pageNumber, pageSize, searchValue, sorts } = this.state
    const pageSizeStr = `&pageSize=${pageSize}`
    const pageNumberStr = `&pageNumber=${pageNumber + this.state.pageIndexBase}`
    const searchStr = searchValue ? `&search=${searchValue}` : ''
    const createdOn = topRightFilter
      ? `&createdOn=${topRightFilter.createdOn}`
      : ''

    if (sorts) {
      const sortArray: string[] = []
      sortStr = '&sorts='
      for (const key in sorts) {
        sortArray.push(`${key} ${sorts[key]}`)
      }

      sortStr += sortArray.join(',')
    }

    const includeQSStart = outURL ? outURL.indexOf('?') === -1 : undefined

    outURL += `${
      includeQSStart ? '?' : ''
    }${searchStr}${sortStr}${pageSizeStr}${pageNumberStr}`

    if (filtersArray && filtersArray.length > 0) {
      outURL += filtersArray.reduce((previous: string, filter: Filter) => {
        return (previous += `&${filter.key}=${filter.value}`)
      }, '')
    }

    outURL += createdOn

    return outURL
  }

  addFilterToFilterArray = (filter: Filter, filterValue: any) => {
    const { filtersArray } = this.state
    if (Array.isArray(filtersArray)) {
      let newState: GQLGridState = {
        ...this.state,
        filtersArray: [
          ...filtersArray,
          {
            ...filter,
            value: filterValue
          }
        ]
      }

      newState = this.addOnFilterChangedState(newState)

      this.setState(newState, () => {
        this.fetchData()
      })
    }
  }

  addOnFilterChangedState = (newState: GQLGridState): GQLGridState => {
    const { onFilterChanged } = this.props

    if (typeof onFilterChanged === 'function') {
      const filterChangeState = onFilterChanged(newState)

      if (typeof filterChangeState !== 'undefined') {
        newState = {
          ...newState,
          ...filterChangeState
        }
      }
    }

    return newState
  }

  removeFilter = (filter: Filter) => {
    const { filtersArray } = this.state

    if (filtersArray) {
      this.setState(
        {
          filtersArray: filtersArray.filter(f => f.key !== filter.key)
        },
        () => {
          this.fetchData()
        }
      )
    }
  }

  getAvailableFilters = () => {
    const filterOptions = Object.values(this.props.availableFilters || {})
    return filterOptions.filter(f => this.state.filters[f.key] === undefined)
  }

  getSelectedFilters = () => {
    const { availableFilters } = this.props
    const { filterOperators, filterValues2, filters } = this.state

    const filterOptions = Object.values(availableFilters || {})
    return filterOptions
      .filter(f => filters.hasOwnProperty(f.key))
      .map(fo => ({
        ...fo,
        label: fo.label,
        operator: filterOperators[fo.key],
        value: filters[fo.key],
        value2: filterValues2[fo.key]
      }))
  }

  handleFilterApplied =
    (refetch: () => void) =>
    (
      filter: Filter,
      value: any, // todo: type this
      operator: any, // todo: type this
      value2?: any // todo: code smell. needs better data type to support n values
    ) => {
      const { filters } = this.state

      if (filter) {
        this.addFilterToFilterArray(filter, value)
      }

      this.setState(
        {
          filterOperators: {
            ...this.state.filterOperators,
            [filter.key]: operator
          },
          filterValues2: {
            ...this.state.filterValues2,
            [filter.key]: value2
          },
          filters: {
            ...filters,
            [filter.key]: value
          },
          pageNumber: 0
        },
        () => {
          this.props.getGridFilters &&
            this.props.getGridFilters(this.state.filters)
          return refetch
        }
      )
    }

  handleFilterRemoved = (refetch: () => void) => (filter: Filter) => {
    const { filterOperators, filterValues2, filters, filtersArray } = this.state
    if (filters) {
      this.setState(
        {
          filterOperators: omit(filterOperators, [filter.key]),
          filterValues2: omit(filterValues2, [filter.key]),
          filters: omit(filters, [filter.key]),
          filtersArray: filtersArray
            ? filtersArray.filter(f => f.key !== filter.key)
            : [],
          pageNumber: 0
        },
        () => {
          this.props.getGridFilters &&
            this.props.getGridFilters(this.state.filters)
          return refetch
        }
      )
    }
  }

  clearSelectedItems = () => {
    this.setSelectedItems([])
  }

  selectAllItems = () => {
    const { data } = this.state
    this.setSelectedItems([...data])
  }

  onRowSelected = (checkboxInfo: CheckboxProps, data: any) => {
    const { gridOptions, onSelectedItemsChange } = this.props
    const { selectedItems = [] } = this.state

    if (checkboxInfo.checked) {
      selectedItems.push(data)
    } else if (gridOptions && gridOptions.selectionKey) {
      const indexToDeselect = selectedItems.findIndex(
        item =>
          item[gridOptions.selectionKey || ''] ===
          data[gridOptions.selectionKey || '']
      )
      if (indexToDeselect >= 0) {
        selectedItems.splice(indexToDeselect, 1)
      }
    }

    this.setState(
      {
        selectedItems
      },
      () => {
        typeof onSelectedItemsChange === 'function' &&
          onSelectedItemsChange(selectedItems)
      }
    )
  }

  onToggleSelectionColumn = (checkboxInfo: CheckboxProps, data: any[]) => {
    const { gridOptions, onSelectedItemsChange } = this.props
    const { selectedItems = [] } = this.state
    data.forEach(row => {
      if (gridOptions && gridOptions.selectionKey) {
        const existingSelectedIndex = selectedItems.findIndex(
          item =>
            item[gridOptions.selectionKey || ''] ===
            row[gridOptions.selectionKey || '']
        )

        if (checkboxInfo.checked && existingSelectedIndex < 0) {
          selectedItems.push(row)
        } else if (!checkboxInfo.checked && existingSelectedIndex >= 0) {
          selectedItems.splice(existingSelectedIndex, 1)
        }
      }
    })

    this.setState(
      {
        selectedItems
      },
      () => {
        typeof onSelectedItemsChange === 'function' &&
          onSelectedItemsChange(selectedItems)
      }
    )
  }

  private setSelectedItems = (items: any[]) => {
    this.setState({ selectedItems: items }, () => {
      const { onSelectedItemsChange } = this.props
      if (typeof onSelectedItemsChange !== 'function') {
        return
      }
      onSelectedItemsChange(this.state.selectedItems || [])
    })
  }

  render() {
    const {
      additionalMenuItems,
      client,
      columns,
      displaySearch,
      entityName,
      fetchPolicy,
      gridOptions,
      hideShowingText,
      isOnePage,
      noResultsFound,
      onSelectedItemsChange,
      pluralEntityName,
      query,
      renderRow,
      searchRowElement,
      selectable,
      style,
      topRightFilter,
      totalGetter
    } = this.props

    const {
      isFetching,
      pageNumber,
      pageSize,
      searchValue,
      selectedItems,
      sorts,
      topRightFilter: topRightFilterState
    } = this.state
    return (
      <Query
        client={client || piBaseClient}
        fetchPolicy={fetchPolicy || 'cache-and-network'}
        query={query}
        variables={this.getVariables()}>
        {({ data, loading, refetch }) => {
          let items = []
          let totalPages = 0
          let total = 0

          if (!this.refetch && refetch) {
            this.refetch = refetch
          }

          if (!loading && !isEmpty(data)) {
            const values = Object.values(data)

            items = (values[0] as any).items

            totalPages = (values[0] as any).totalPages

            if (totalGetter) {
              total = totalGetter(data)
            }
          }
          return (
            <div>
              <Grid
                {...gridOptions}
                additionalMenuItems={additionalMenuItems}
                columns={columns}
                count={total}
                data={items}
                displaySearch={displaySearch}
                entityName={entityName || 'item'}
                filters={this.getAvailableFilters()}
                hideShowingText={hideShowingText}
                isFetching={isFetching || loading}
                noResultsFound={noResultsFound}
                onColumnClicked={this.handleColumnClicked(refetch)}
                onFilterApplied={this.handleFilterApplied(refetch)}
                onFilterRemoveClicked={this.handleFilterRemoved(refetch)}
                onRowSelected={this.onRowSelected}
                onSearchChanged={this.handleSearchChanged(refetch)}
                onSelectedItemsChange={onSelectedItemsChange}
                onToggleSelectionColumn={this.onToggleSelectionColumn}
                onTopRightFilterChanged={this.handleTopRightFilterChanged(
                  refetch
                )}
                pluralEntityName={pluralEntityName || 'items'}
                renderRow={renderRow}
                renderTableSubHeader={p => {
                  if (
                    gridOptions &&
                    typeof gridOptions === 'object' &&
                    typeof gridOptions.renderTableSubHeader === 'function'
                  ) {
                    return gridOptions.renderTableSubHeader(
                      { ...p, ...this.props } as any,
                      {
                        clearSelectedItems: this.clearSelectedItems,
                        selectAllItems: this.selectAllItems
                      }
                    )
                  }
                  return null
                }}
                searchRowElement={searchRowElement}
                searchValue={searchValue}
                selectable={selectable}
                selectedFilters={this.getSelectedFilters()}
                selectedItems={selectedItems}
                sorted={sorts}
                style={{ margin: '2rem 0', ...style }}
                topRightFilter={
                  topRightFilter && topRightFilterState
                    ? {
                        ...topRightFilter,
                        value: topRightFilterState[topRightFilter.key]
                      }
                    : undefined
                }
              />
              {!isOnePage && (
                <Pagination
                  onPageChanged={this.handlePageChanged(refetch)}
                  onPageSizeChanged={this.handlePageSizeChanged(refetch)}
                  page={pageNumber + 1}
                  pageSize={pageSize}
                  totalPages={totalPages}
                />
              )}
            </div>
          )
        }}
      </Query>
    )
  }

  private handleSearchChanged = (refetch: () => void) => (e: any) => {
    this.setState({
      pageNumber: 0,
      searchValue: e.target.value
    })
    this.searchValue(e.target.value, refetch)
    this.props.getSearchValue && this.props.getSearchValue(e.target.value)
  }

  private searchValue = debounce((value: string, refetch: any) => {
    this.setState(
      {
        valueToBeSearched: value
      },
      refetch
    )
  }, 750)
}

type SortDirection = 'ASC' | 'DESC'
